import { useMemo } from 'react';
import useSWR from 'swr';

import { assetsServiceInstance } from '@/api';
import { IDealerCenterData, IVinCitiesResponse } from '@/types/dealers';
import { uniq, formatAddress } from '@/utils/common';

const getDealerships = async (endpoint: string) => {
  const { data } = await assetsServiceInstance.get<IDealerCenterData[]>(endpoint);
  return data;
};

const getVincities = async (endpoint: string) => {
  const { data } = await assetsServiceInstance.get<IVinCitiesResponse>(endpoint);
  return data;
};

export type TDealership = {
  id: IDealerCenterData['id'];
  name: string;
  fullName: string;
  city: string;
  address: string;
};

const useDealershipDataFetch = (logisticStatusId: string, brand: string | undefined) => {
  const isJaecoo = brand?.toLowerCase() === 'Jaecoo'.toLowerCase();
  const { data: dealershipData, error } = useSWR(
    isJaecoo ? 'dealerships_jaecoo.json' : 'dealerships.json',
    getDealerships,
  );
  const { data: vincities, error: error2 } = useSWR(
    isJaecoo ? 'vincities_jaecoo.json' : 'vincities.json',
    getVincities,
  );

  const IMPORTANT_CITIES = ['Москва', 'Санкт-Петербург'];

  const { dealerships, cities } = useMemo(() => {
    const vinCity = vincities?.warehouses?.find(vinCity =>
      Object.values(vinCity.id).includes(logisticStatusId),
    );

    const filteredDealerships = vinCity
      ? (dealershipData || [])
          .filter(dealer => {
            if (vinCity?.dealers) {
              return vinCity.dealers.includes(dealer.id);
            }

            return true;
          })
          .map<TDealership>(({ id, name, address }) => {
            const formattedAddress = formatAddress(address);
            return {
              id,
              name,
              fullName: [name, formattedAddress].join(', '),
              address: formattedAddress,
              city: address.city.name,
            };
          })
      : [];
    return {
      dealerships: filteredDealerships,
      cities: uniq(filteredDealerships.map(({ city }) => city))
        .sort((a, b) => {
          if (IMPORTANT_CITIES.some(c => a.toLowerCase().includes(c.toLowerCase()))) {
            return -1;
          }
          if (IMPORTANT_CITIES.some(c => b.toLowerCase().includes(c.toLowerCase()))) {
            return 1;
          }
          if (a.toLowerCase() > b.toLowerCase()) {
            return 1;
          }
          if (b.toLowerCase() > a.toLowerCase()) {
            return -1;
          }
          return 0;
        })
        .filter(city => {
          if (vinCity?.cities) {
            return vinCity.cities.includes(city);
          }

          return true;
        }),
    };
  }, [dealershipData, vincities]);

  const getDealershipsByCity = (city?: string | null) => {
    if (!city) return [];
    return dealerships
      .filter(d => d.city.toLowerCase() === city.toLowerCase())
      .sort((a, b) => {
        if (a.fullName.toLowerCase() > b.fullName.toLowerCase()) {
          return 1;
        }
        if (b.fullName.toLowerCase() > a.fullName.toLowerCase()) {
          return -1;
        }
        return 0;
      });
  };

  return {
    data: { dealerships, cities },
    getDealershipsByCity,
    loading: !dealershipData ? true : !vincities,
    error,
  };
};

export default useDealershipDataFetch;
